import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, RefreshCw, Search, Sparkles, Star, X } from 'lucide-react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Button } from '../components/ui/Button'
import { useToast } from '../components/ui/Toast'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { SkeletonList } from '../components/ui/Skeleton'
import { useTheme } from '../components/providers/ThemeProvider'
import { useBranding } from '../hooks/useBranding'
import { useUserTimezone } from '../hooks/useUserTimezone'
import { DreamService, type Dream, type DreamBoardCard } from '../services/dreamService'
import { lifeAreaLabel } from '../utils/taskForm'
import { cleanupWords } from '../utils/dreamWords'
import { groupDreams, matchesQuery, summarize, summaryWords, type DreamGroupBy, type DreamSortBy, type SortDir } from '../utils/dreamSections'
import { DreamRow } from '../components/dreams/DreamRow'
import { TaskQuickAdd } from '../components/tasks/TaskQuickAdd'

/**
 * The dream list (Dreams UX pass 2026-09-06), read the way a person reads
 * it — what am I moving toward, and is anything moving:
 *
 *   - Logo, one title, one summary line, two verbs — the task list's frame.
 *   - A quick-add row: a name is enough; it lands under "Needs a journey".
 *   - Sections by movement: Ready to check · In motion · Needs a journey ·
 *     Waiting for you · Paused · Done (collapsed). Life area / None one click away.
 *   - A row is a progress mark, a title, one quiet line: the next thing under
 *     it and which goal it is for. Tap the mark to finish the dream (with Undo).
 *     The rest under ⋯.
 *   - Search instead of pagination; group, sort and direction remembered.
 */
const GROUP_PREF = 'dreams.groupBy'
const SORT_PREF = 'dreams.sortBy'
const DIR_PREF = 'dreams.sortDir'
const pad = (n: number) => String(n).padStart(2, '0')
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return (allowed as readonly string[]).includes(v || '') ? (v as T) : fallback
  } catch { return fallback }
}
function writePref(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* per-viewer convenience only */ }
}

export const Dreams: React.FC = () => {
  const { isDiary: diaryMode } = useTheme()
  const { assistant: personaName } = useBranding()
  const { timezone } = useUserTimezone()
  const navigate = useNavigate()
  const toast = useToast()
  const [dreams, setDreams] = useState<Dream[]>([])
  const [cards, setCards] = useState<Record<string, DreamBoardCard | undefined>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [boardIssue, setBoardIssue] = useState(false)
  const [query, setQuery] = useState('')
  const [groupBy, setGroupByState] = useState<DreamGroupBy>(() => readPref(GROUP_PREF, ['movement', 'area', 'none'] as const, 'movement'))
  const [sortBy, setSortByState] = useState<DreamSortBy>(() => readPref(SORT_PREF, ['attention', 'progress', 'created', 'updated'] as const, 'attention'))
  const [sortDir, setSortDirState] = useState<SortDir>(() => readPref(DIR_PREF, ['asc', 'desc'] as const, 'desc'))
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [deleteTarget, setDeleteTarget] = useState<Dream | null>(null)
  const [today, setToday] = useState(todayKey)
  const dreamService = useMemo(() => new DreamService(), [])

  useProvideFeedbackContext({ pageType: 'dreams_list', extra: { groupBy, sortBy, sortDir, searching: Boolean(query) } })

  const setGroupBy = (g: DreamGroupBy) => { setGroupByState(g); writePref(GROUP_PREF, g) }
  const setSortBy = (s: DreamSortBy) => { setSortByState(s); writePref(SORT_PREF, s) }
  const setSortDir = (d: SortDir) => { setSortDirState(d); writePref(DIR_PREF, d) }

  const load = useCallback(async () => {
    const day = todayKey()
    setToday(day)
    try {
      setError(null)
      const [rows, board] = await Promise.all([
        dreamService.getDreams({ include: 'all' }),
        dreamService.getBoard(day).then((b) => { setBoardIssue(false); return b }).catch(() => { setBoardIssue(true); return null }),
      ])
      setDreams(Array.isArray(rows) ? rows : [])
      setCards(board?.dreams || {})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dreams')
    } finally {
      setLoading(false)
    }
  }, [dreamService])

  useEffect(() => { void load() }, [load])

  const visible = useMemo(() => dreams.filter((d) => matchesQuery(d, query)), [dreams, query])
  const groups = useMemo(() => groupDreams(visible, cards, groupBy, today, lifeAreaLabel, { by: sortBy, dir: sortDir }), [visible, cards, groupBy, today, sortBy, sortDir])
  const summary = useMemo(() => summarize(dreams, cards, today), [dreams, cards, today])

  // ── verbs ──
  const setStatus = async (dream: Dream, status: 'completed' | 'active', restore?: { status: string; progress: number }) => {
    try {
      await dreamService.updateDream(dream.id, restore ? { status: restore.status, progress: restore.progress } : status === 'completed' ? { status, progress: 100 } : { status })
      await load()
      if (status === 'completed') toast.toast({ variant: 'success', title: 'Done', description: dream.title, action: { label: 'Undo', onClick: () => void setStatus(dream, 'active', { status: dream.status || 'active', progress: dream.progress }) } })
      else toast.success('Back on', dream.title)
    } catch (err) {
      toast.error('Could not record that', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    const target = deleteTarget
    try {
      const result = await dreamService.deleteDream(target.id)
      setDeleteTarget(null)
      await load()
      toast.toast({ variant: 'success', title: 'Dream deleted', description: cleanupWords(result.cleanup) })
    } catch (err) {
      setDeleteTarget(null)
      toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const quickAdd = async (title: string) => {
    const created = await dreamService.createDream({ title, priority: 'medium' })
    await load()
    toast.toast({ variant: 'success', title: 'Added', description: title, action: created?.id ? { label: 'Plan the journey', onClick: () => navigate(`/dreams/${created.id}`) } : undefined })
  }

  const toggle = (key: string, def: boolean) => setCollapsed((c) => ({ ...c, [key]: !(c[key] ?? def) }))

  // ── frame ──
  if (loading) return <div className="space-y-4" aria-busy="true" aria-label="Loading dreams"><SkeletonList count={5} /></div>
  if (error && dreams.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center" role="alert">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
          <h2 className="mb-2 text-xl font-semibold text-primary">The list would not load</h2>
          <p className="mb-4 text-secondary">{error}</p>
          <Button onClick={() => { setLoading(true); void load() }}><RefreshCw className="mr-2 h-4 w-4" />Try again</Button>
        </div>
      </div>
    )
  }

  const empty = dreams.length === 0
  const nothingMatches = !empty && visible.length === 0

  return (
    <div className="space-y-4" data-testid="dreams-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand sm:h-10 sm:w-10" aria-hidden="true" data-testid="dreams-logo">
            <Star className="h-5 w-5 text-on-brand" />
          </span>
          <div className="min-w-0">
            <h1 className={`text-xl font-semibold leading-tight text-primary sm:text-2xl ${diaryMode ? 'font-handwritten' : ''}`}>Dreams</h1>
            <p className="text-app-meta text-secondary" data-testid="dreams-summary">{summaryWords(summary)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="xs" onClick={() => navigate('/dreams/create')} data-testid="dreams-new"><Plus className="mr-1 h-4 w-4" />New dream</Button>
          <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=dreams&type=dream')} data-testid="dreams-ask"><Sparkles className="mr-1 h-4 w-4" />{personaName}</Button>
        </div>
      </div>

      {boardIssue && !empty && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-tint-bad-edge bg-tint-bad-soft p-3 text-bad" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="text-app-meta">We could not load the goals under them, so the rows show the dreams only.</span>
          <Button onClick={() => void load()} variant="outline" size="xs" className="ml-auto shrink-0"><RefreshCw className="mr-1.5 h-4 w-4" />Try again</Button>
        </div>
      )}

      {!empty && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="relative block w-full sm:w-64 lg:w-80">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a dream" aria-label="Find a dream"
                   className="min-h-touch w-full rounded-md border border-soft bg-surface pl-8 pr-8 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30 lg:min-h-0 lg:py-1.5" data-testid="dreams-search" />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted hover:text-primary"><X className="h-3.5 w-3.5" /></button>}
          </label>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-app-micro">
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Group by</span>
              <div role="radiogroup" aria-label="Group by" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['movement', 'Movement'], ['area', 'Life area'], ['none', 'None']] as Array<[DreamGroupBy, string]>).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={groupBy === k} onClick={() => setGroupBy(k)} className={`rounded px-2 py-1 font-medium transition-colors ${groupBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`} data-testid={`dreams-group-${k}`}>{label}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Sort</span>
              <div role="radiogroup" aria-label="Sort" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['attention', 'Attention'], ['progress', 'Progress'], ['created', 'Created'], ['updated', 'Updated']] as Array<[DreamSortBy, string]>).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={sortBy === k} onClick={() => setSortBy(k)} className={`rounded px-2 py-1 font-medium transition-colors ${sortBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`} data-testid={`dreams-sort-${k}`}>{label}</button>
                ))}
              </div>
              {sortBy !== 'attention' && (
                <button type="button" onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')} aria-label={sortDir === 'desc' ? 'Highest first. Switch to lowest first' : 'Lowest first. Switch to highest first'}
                        className="inline-flex items-center gap-0.5 rounded-md border border-soft bg-surface px-1.5 py-1 font-medium text-secondary hover:text-primary" data-testid="dreams-sort-dir">
                  {sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                  {sortBy === 'progress' ? (sortDir === 'desc' ? 'Furthest along' : 'Just starting') : (sortDir === 'desc' ? 'Newest' : 'Oldest')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <TaskQuickAdd onAdd={quickAdd} onMore={(title) => navigate('/dreams/create', { state: title ? { title } : undefined })} autoFocus={empty} placeholder="Add a dream — a name is enough" />

      {empty && (
        <div className="rounded-xl border border-soft bg-surface p-6 text-center" data-testid="dreams-empty">
          <p className="text-app-body font-semibold text-primary">Nothing here yet</p>
          <p className="mx-auto mt-1 max-w-md text-app-meta text-secondary">
            Put one thing you want your life to hold in the box above — a name is enough. The journey there, a few goals in order, can be planned on the dream itself.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=dreams&type=dream')}><Sparkles className="mr-1 h-4 w-4" />Build with {personaName}</Button>
          </div>
        </div>
      )}

      {nothingMatches && (
        <p className="text-app-meta text-muted" data-testid="dreams-no-match">Nothing matches “{query}”. <button type="button" onClick={() => setQuery('')} className="text-brand hover:underline">Clear the search</button></p>
      )}

      {!empty && !nothingMatches && groups.map((g) => {
        const isCollapsed = collapsed[g.key] ?? Boolean(g.collapsedByDefault)
        const Chevron = isCollapsed ? ChevronRight : ChevronDown
        return (
          <section key={g.key} data-testid={`dreams-section-${g.key}`} aria-labelledby={`dreams-h-${g.key}`}>
            <button type="button" onClick={() => toggle(g.key, Boolean(g.collapsedByDefault))} aria-expanded={!isCollapsed} className="flex w-full items-center gap-1.5 rounded py-1 text-left hover:text-brand" data-testid="dreams-section-toggle">
              <Chevron className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden="true" />
              <h2 id={`dreams-h-${g.key}`} className="text-app-meta font-bold uppercase tracking-widest text-secondary">{g.title}</h2>
              <span className="text-app-micro text-muted" data-testid="dreams-section-count">{g.items.length}</span>
            </button>
            {!isCollapsed && g.items.length > 0 && (
              <div className="mt-1 space-y-1.5">
                {g.items.map((dream) => (
                  <DreamRow key={dream.id} dream={dream} card={cards[String(dream.id)]} showArea={groupBy !== 'area'} tz={timezone}
                            timeHint={sortBy === 'created' || sortBy === 'updated' ? sortBy : null}
                            onComplete={(d) => setStatus(d, 'completed')} onReopen={(d) => setStatus(d, 'active')} onDelete={setDeleteTarget} diaryMode={diaryMode} />
                ))}
              </div>
            )}
            {!isCollapsed && g.items.length === 0 && g.quiet && <p className="mt-0.5 pl-5 text-app-meta text-muted" data-testid="dreams-section-quiet">{g.quiet}</p>}
          </section>
        )
      })}

      <ConfirmationModal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} onConfirm={confirmDelete} title="Delete this dream?"
                         message={`“${deleteTarget?.title}” will be removed, along with the journey goals not yet started. The other goals under it stay, on their own.`} variant="error" confirmText="Delete" />
    </div>
  )
}

export default Dreams
